/**
 * Qué número mostrarle al asesor — y qué decirle cuando no hay ninguno.
 *
 * `phone` es la DIRECCIÓN con la que el sistema le habla al cliente, y desde
 * que WhatsApp tiene nombres de usuario no siempre es un teléfono: para un
 * cliente con alias vale su BSUID (`PE.2541105976362984`), porque Meta deja de
 * mandar el número. `contact_phone` es el teléfono de verdad, o vacío mientras
 * el cliente no lo dé.
 *
 * Pintar `phone` a secas le pone al asesor `PE.2541105976362984` donde espera
 * un celular: parece un dato corrupto, y sobre todo no dice lo único que él
 * necesita saber — que a esta persona **no la puede llamar** todavía.
 *
 * El backend equivalente es `lambdas/shared/identidad.py`.
 */

/** `PE.2541105976362984` o `PE.ENT.11815799212886844830`, no un teléfono. */
export function esBsuid(valor?: string | null): boolean {
  return /^[A-Za-z]{2}\.(ENT\.)?\d+$/.test((valor ?? '').trim())
}

export interface ConIdentidad {
  phone?: string | null
  contact_phone?: string | null
  /** El BSUID, aparte de la dirección: lo trae el detalle (DEF-018). */
  bsuid?: string | null
  /** El celular que el cliente escribió y confirmó en el precalificador del
   *  socio. Se muestra con su origen; `telefonoDe` no lo devuelve porque no
   *  es el número de su WhatsApp. */
  celular_declarado?: string | null
}

/** El celular del precalificador, si lo hay y no hay teléfono real. */
function declarado(c: ConIdentidad): string {
  return (c.celular_declarado ?? '').trim()
}

/** El teléfono para mostrar, o `''` si no hay ninguno que mostrar. */
export function telefonoDe(c: ConIdentidad): string {
  const real = (c.contact_phone ?? '').trim()
  if (real) return real
  const direccion = (c.phone ?? '').trim()
  return esBsuid(direccion) ? '' : direccion
}

/**
 * Lo que va en pantalla: el teléfono, o por qué no lo hay.
 *
 * Se distinguen los dos casos vacíos a propósito. «Sin número» a secas haría
 * pensar en un dato que se perdió; el cliente con alias es una situación
 * normal y con salida — el bot ya le pidió el número, y cuando lo dé aparece
 * acá solo.
 */
export function telefonoVisible(c: ConIdentidad): string {
  const tel = telefonoDe(c)
  if (tel) return tel
  if (declarado(c)) return `${declarado(c)} · del precalificador`
  return esBsuid(c.phone) ? 'Sin número — cuenta con alias' : 'Sin número'
}

/**
 * El «Teléfono» de la Ficha: el número, o por qué no lo hay (DEF-018).
 *
 * QA de TEST, 02-oct-2026: la Ficha pintaba `phone` a secas y mostraba
 * «Teléfono: PE.1569326808263603» — el BSUID, la dirección de envío —, el
 * único dato de la ficha que no sirve para lo que existe: no se puede llamar
 * ni cruzar con el lead. La redacción es la del informe de derivación
 * (`conversation/_derivar_teleagent`), para que el asesor lea lo mismo en los
 * dos lugares. La cabecera, más corta, sigue con `telefonoVisible`.
 */
export function telefonoDeFicha(c: ConIdentidad): string {
  const tel = telefonoDe(c)
  if (tel) return tel
  if (declarado(c)) return `${declarado(c)} (lo escribió y confirmó en el precalificador)`
  return esBsuid(c.phone) || esBsuid(c.bsuid) ? 'No disponible (cliente con alias)' : 'No disponible'
}
